import React, { useEffect, useRef, useState } from 'react';
import { type Address } from '../shared/types';
import proj4 from 'proj4';
import { parseSignDescription, nySpcs, wgs84, SOCRATA_BASE_URL, DAY_MAP } from '../api/sweepAPI';
import { geocodeAddress } from '../api/geocodeUtils';
import { Plus, X } from 'lucide-react';

interface MapProps {
  addresses: Address[];
  onSignClick?: (address: Address) => void;
}

const Map: React.FC<MapProps> = ({ addresses, onSignClick }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set(['MO', 'TU', 'WE', 'TH', 'FR']));
  const [exactMatch, setExactMatch] = useState(false);
  const [selectedSign, setSelectedSign] = useState<{
    address: Address;
    description: string;
  } | null>(null);

  const days = [
    { id: 'MO', label: 'MON', color: 'bg-mta-blue' },
    { id: 'TU', label: 'TUE', color: 'bg-mta-orange' },
    { id: 'WE', label: 'WED', color: 'bg-mta-green' },
    { id: 'TH', label: 'THU', color: 'bg-mta-red' },
    { id: 'FR', label: 'FRI', color: 'bg-mta-purple' },
    { id: 'SA', label: 'SAT', color: 'bg-mta-yellow' },
    { id: 'SU', label: 'SUN', color: 'bg-mta-gray' }
  ];

  const toggleDay = (day: string) => {
    const newDays = new Set(selectedDays);
    if (newDays.has(day)) {
      newDays.delete(day);
    } else {
      newDays.add(day);
    }
    setSelectedDays(newDays);
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  };

  const fetchAbortRef = useRef<AbortController | null>(null);
  const fetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSigns = () => {
    if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(async () => {
      try {
        if (!mapInstanceRef.current || selectedDays.size === 0) return;

        fetchAbortRef.current?.abort();
        const controller = new AbortController();
        fetchAbortRef.current = controller;

        clearMarkers();

        const mapBounds = mapInstanceRef.current.getBounds();
        if (!mapBounds) return;

        const ne = mapBounds.getNorthEast();
        const sw = mapBounds.getSouthWest();

        const [neX, neY] = proj4(wgs84, nySpcs, [ne.lng(), ne.lat()]);
        const [swX, swY] = proj4(wgs84, nySpcs, [sw.lng(), sw.lat()]);

        const response = await fetch(
          `${SOCRATA_BASE_URL}?$where=sign_x_coord >= ${Math.min(swX, neX)} AND sign_x_coord <= ${Math.max(swX, neX)} AND sign_y_coord >= ${Math.min(swY, neY)} AND sign_y_coord <= ${Math.max(swY, neY)} AND record_type = 'Current'&$limit=500`,
          { signal: controller.signal }
        );

        if (!response.ok) throw new Error('Failed to fetch signs');

        const signs = await response.json();

        signs.forEach((sign: any) => {
          const daysInSign = sign.sign_description.match(/MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY/gi) || [];
          const signDays = daysInSign.map((day: string) => DAY_MAP[day.toUpperCase()]);

          const matchesFilter = exactMatch
            ? signDays.length === selectedDays.size && signDays.every((day: string) => selectedDays.has(day))
            : signDays.some((day: string) => selectedDays.has(day));

          if (matchesFilter) {
            const [lng, lat] = proj4(nySpcs, wgs84, [parseFloat(sign.sign_x_coord), parseFloat(sign.sign_y_coord)]);
            const marker = new google.maps.Marker({
              map: mapInstanceRef.current,
              position: { lat, lng },
              title: `${sign.on_street} (${sign.side_of_street})\n${sign.sign_description}`,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 8,
                fillColor: '#FCCC0A',
                fillOpacity: 1,
                strokeWeight: 3,
                strokeColor: '#000000'
              }
            });

            marker.addListener('click', () => {
              const schedule = parseSignDescription(sign.sign_description);
              const newAddr: Address = {
                id: Date.now(),
                address: `${sign.on_street} (${sign.side_of_street})`,
                regulations: [{
                  id: sign.order_number ?? String(Date.now()),
                  street: sign.on_street,
                  fromStreet: sign.from_street,
                  toStreet: sign.to_street,
                  side: sign.side_of_street,
                  schedule,
                  coordinates: { x: parseFloat(sign.sign_x_coord), y: parseFloat(sign.sign_y_coord) },
                  displayText: `${sign.from_street} to ${sign.to_street} - ${schedule.days.join('/')} ${schedule.startTime}-${schedule.endTime}`
                }]
              };
              setSelectedSign({
                address: newAddr,
                description: `${sign.on_street} (${sign.side_of_street})\n${sign.sign_description}`
              });
            });

            markersRef.current.push(marker);
          }
        });
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching signs:', error);
        }
      }
    }, 250);
  };

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    try {
      if (!mapInstanceRef.current) {
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          zoom: 14,
          center: { lat: 40.7128, lng: -74.0060 },
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          maxZoom: 16,
          minZoom: 12,
          styles: [
            { elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
            { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a1a" }] },
            { elementType: "labels.text.fill", stylers: [{ color: "#808183" }] },
            { featureType: "road", elementType: "geometry", stylers: [{ color: "#2d2d2d" }] },
            { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1a1a1a" }] },
            { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FCCC0A" }] },
            { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#000000" }] },
            { featureType: "water", elementType: "geometry", stylers: [{ color: "#0039A6" }] },
            { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#00933C" }] },
            { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2d2d2d" }] },
            { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#FCCC0A" }] }
          ]
        });
      }

      const bounds = new google.maps.LatLngBounds();

      Promise.all(addresses.map(addr =>
        geocodeAddress(addr.address).then(r => new google.maps.LatLng(r.lat, r.lng))
      ))
        .then(locations => {
          locations.forEach(location => {
            bounds.extend(location);
          });

          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latPadding = (ne.lat() - sw.lat()) * 0.2;
          const lngPadding = (ne.lng() - sw.lng()) * 0.2;
          bounds.extend(new google.maps.LatLng(ne.lat() + latPadding, ne.lng() + lngPadding));
          bounds.extend(new google.maps.LatLng(sw.lat() - latPadding, sw.lng() - lngPadding));

          mapInstanceRef.current?.fitBounds(bounds);
          fetchSigns();
        })
        .catch(error => {
          console.error('Error setting up map:', error);
        });
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  }, [addresses]);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    fetchSigns();
    const listener = mapInstanceRef.current.addListener('idle', fetchSigns);

    return () => {
      google.maps.event.removeListener(listener);
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      fetchAbortRef.current?.abort();
      clearMarkers();
    };
  }, [selectedDays, exactMatch]);

  return (
    <div className="card-brutalist">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-mta-white">
        <div className="w-8 h-8 bg-mta-green rounded-full flex items-center justify-center">
          <span className="text-mta-white font-bold text-sm">3</span>
        </div>
        <div>
          <h2 className="text-lg font-bold text-mta-white uppercase tracking-wide font-helvetica">
            Neighborhood Map
          </h2>
          <p className="text-mta-silver text-xs uppercase tracking-wide">
            Click yellow dots to add locations
          </p>
        </div>
      </div>

      <div
        ref={mapRef}
        className="w-full h-[400px] border-3 border-mta-white"
      />

      {selectedSign && (
        <div className="mt-4 bg-mta-black border-3 border-mta-yellow p-4">
          <p className="text-sm text-mta-white mb-4 font-helvetica whitespace-pre-line">
            {selectedSign.description}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => {
                onSignClick?.(selectedSign.address);
                setSelectedSign(null);
              }}
              className="btn-brutalist btn-mta-yellow flex-1 flex items-center justify-center gap-2 text-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={3} />
              Add to Calendar
            </button>
            <button
              onClick={() => setSelectedSign(null)}
              className="btn-brutalist bg-mta-gray text-mta-white border-3 border-mta-black flex items-center justify-center px-4"
            >
              <X className="w-4 h-4" strokeWidth={3} />
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap gap-2 justify-center">
          {days.map(day => (
            <button
              key={day.id}
              onClick={() => toggleDay(day.id)}
              className={`w-12 h-12 font-bold text-xs uppercase tracking-wide border-3 transition-all ${
                selectedDays.has(day.id)
                  ? `${day.color} text-mta-white border-mta-white`
                  : 'text-mta-gray border-mta-gray hover:border-mta-white'
              }`}
              style={selectedDays.has(day.id) ? { boxShadow: '2px 2px 0px 0px #FFFFFF' } : {}}
            >
              {day.label}
            </button>
          ))}
        </div>
        
        <div className="flex justify-center">
          <label className="flex items-center gap-3 cursor-pointer group">
            <div 
              className={`w-6 h-6 border-3 flex items-center justify-center transition-all ${
                exactMatch 
                  ? 'bg-mta-yellow border-mta-yellow' 
                  : 'bg-mta-black border-mta-white group-hover:border-mta-yellow'
              }`}
              onClick={() => setExactMatch(!exactMatch)}
            >
              {exactMatch && <span className="text-mta-black font-bold">✓</span>}
            </div>
            <span className="text-sm text-mta-silver uppercase tracking-wide">
              Exact match only
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default Map;
